import type {SketchDimensionSnapshot} from '../SketchDimensionTypes';
import {resolveEllipseAnchorPointToward} from '../utils';

/** 布局二维坐标（草图局部，米）。 */
export type SketchDimensionLayoutVec2 = readonly [number, number];

/** 引用几何锚点（渲染器按引用持久 ID 解析后的纯数据投影）。 */
export type SketchDimensionReferenceAnchor =
    | {readonly kind: 'point'; readonly position: SketchDimensionLayoutVec2}
    | {readonly kind: 'line'; readonly start: SketchDimensionLayoutVec2; readonly end: SketchDimensionLayoutVec2}
    | {readonly kind: 'circle'; readonly center: SketchDimensionLayoutVec2; readonly radius: number}
    | {
        readonly kind: 'ellipse';
        /** 椭圆中心（草图局部坐标）。 */
        readonly center: SketchDimensionLayoutVec2;
        /** 长轴正向基准点（决定长轴方向与半长轴）。 */
        readonly major: SketchDimensionLayoutVec2;
        /** 短半轴长度（米）。 */
        readonly semiMinor: number;
        /** 弧起始角（相对长轴正向，弧度；THREE.EllipseCurve 语义）。 */
        readonly startAngle: number;
        /** 弧终止角（相对长轴正向，弧度；THREE.EllipseCurve 语义）。 */
        readonly endAngle: number;
        /** 是否顺时针采样（THREE.EllipseCurve 语义）。 */
        readonly clockwise: boolean;
    }
    | {readonly kind: 'parabola'; readonly apex: SketchDimensionLayoutVec2; readonly focal: SketchDimensionLayoutVec2}
    | {readonly kind: 'polyline'; readonly points: readonly SketchDimensionLayoutVec2[]};

/** 直线段。 */
export interface SketchDimensionLayoutSegment {
    readonly start: SketchDimensionLayoutVec2;
    readonly end: SketchDimensionLayoutVec2;
}

/** 圆弧段。 */
export interface SketchDimensionLayoutArc {
    readonly center: SketchDimensionLayoutVec2;
    readonly radius: number;
    /** 起始角（弧度）。 */
    readonly startAngle: number;
    /** 终止角（弧度，按逆时针扫过；小于 startAngle 时加 2π 归一）。 */
    readonly endAngle: number;
}

/** 箭头（位置 + 单位朝向）。 */
export interface SketchDimensionLayoutArrow {
    readonly position: SketchDimensionLayoutVec2;
    readonly direction: SketchDimensionLayoutVec2;
}

/** 尺寸布局纯数据。 */
export interface SketchDimensionLayout {
    /** 尺寸线与尺寸界线直线段。 */
    readonly segments: readonly SketchDimensionLayoutSegment[];
    /** 角度弧段。 */
    readonly arcs: readonly SketchDimensionLayoutArc[];
    /** 箭头。 */
    readonly arrows: readonly SketchDimensionLayoutArrow[];
    /** 文字位置（= 标注位置）。 */
    readonly textPosition: SketchDimensionLayoutVec2;
    /**
     * 辅助文本（主值之外的固定文案，如尺寸链基准的"0"标注）。
     *
     * 渲染器为每条维护 0-N 个副 CSS2D 标签；缺省无。
     */
    readonly auxiliaryLabels?: readonly {
        readonly position: SketchDimensionLayoutVec2;
        readonly text: string;
    }[];
}

/** 箭头几何长度（米）。 */
// ponytail: 箭头/界线缝隙用固定几何长度（2.5mm），缩放时视觉大小随几何变化；
// 屏幕定长箭头需要每帧按相机换算，归阶段 7 显示属性完善。
const ARROW_LENGTH = 0.0025;

/** 向量工具。 */
const subtract = (a: SketchDimensionLayoutVec2, b: SketchDimensionLayoutVec2): SketchDimensionLayoutVec2 => [a[0] - b[0], a[1] - b[1]];
const add = (a: SketchDimensionLayoutVec2, b: SketchDimensionLayoutVec2): SketchDimensionLayoutVec2 => [a[0] + b[0], a[1] + b[1]];
const scale = (a: SketchDimensionLayoutVec2, s: number): SketchDimensionLayoutVec2 => [a[0] * s, a[1] * s];
const dot = (a: SketchDimensionLayoutVec2, b: SketchDimensionLayoutVec2): number => a[0] * b[0] + a[1] * b[1];
const norm = (a: SketchDimensionLayoutVec2): number => Math.hypot(a[0], a[1]);
const normalize = (a: SketchDimensionLayoutVec2): SketchDimensionLayoutVec2 | null => {
    const length = norm(a);

    return length < 1e-12 ? null : [a[0] / length, a[1] / length];
};

/** 空布局（引用几何不可解析时的显式降级：只显示文字）。 */
function createTextOnlyLayout(textPosition: SketchDimensionLayoutVec2): SketchDimensionLayout {
    return {segments: [], arcs: [], arrows: [], textPosition};
}

/** 构造线性尺寸布局（两锚点 + 测量方向 + 标注位置）。 */
function createLinearLayout(
    first: SketchDimensionLayoutVec2,
    second: SketchDimensionLayoutVec2,
    direction: SketchDimensionLayoutVec2,
    label: SketchDimensionLayoutVec2,
    hideExtensionLines = false,
): SketchDimensionLayout {
    /**
     * 尺寸线 = 把两锚点投影到"过标注位置、沿测量方向"的直线上；
     * 尺寸界线 = 锚点到各自投影点（`display.extensionLinesHidden` 时省略）。
     */
    const project = (anchor: SketchDimensionLayoutVec2): SketchDimensionLayoutVec2 => {
        const offset = subtract(anchor, label);

        return add(label, scale(direction, dot(offset, direction)));
    };
    const firstEnd = project(first);
    const secondEnd = project(second);
    const segments: SketchDimensionLayoutSegment[] = [
        {start: firstEnd, end: secondEnd},
    ];

    if (!hideExtensionLines && norm(subtract(first, firstEnd)) > 1e-9) {
        segments.push({start: first, end: firstEnd});
    }
    if (!hideExtensionLines && norm(subtract(second, secondEnd)) > 1e-9) {
        segments.push({start: second, end: secondEnd});
    }

    /**
     * 文字移出标注区间时延长尺寸线至文字（对齐 SOLIDWORKS：避免数字悬浮、
     * 尺寸所属意义不明）。label 与两端点共线（同在过 label 的测量方向直线上），
     * 沿 direction 以 label 为原点参数化，label 落在 [min,max] 外则从最近端点补延长段。
     */
    const tFirst = dot(subtract(firstEnd, label), direction);
    const tSecond = dot(subtract(secondEnd, label), direction);
    const tMin = Math.min(tFirst, tSecond);
    const tMax = Math.max(tFirst, tSecond);

    if (tMin > 1e-9) {
        segments.push({start: tFirst <= tSecond ? firstEnd : secondEnd, end: label});
    } else if (tMax < -1e-9) {
        segments.push({start: tFirst >= tSecond ? firstEnd : secondEnd, end: label});
    }

    const inward = normalize(subtract(secondEnd, firstEnd));
    const arrows: SketchDimensionLayoutArrow[] = inward
        ? [
            {position: firstEnd, direction: inward},
            {position: secondEnd, direction: scale(inward, -1)},
        ]
        : [];

    return {segments, arcs: [], arrows, textPosition: label};
}

/** 把角度规约到 (−π, π]。 */
function wrapToPi(angle: number): number {
    const wrapped = wrapTwoPi(angle);

    return wrapped > Math.PI ? wrapped - Math.PI * 2 : wrapped;
}

/** 把角度规约到 [0, 2π)。 */
function wrapTwoPi(angle: number): number {
    let wrapped = angle % (Math.PI * 2);

    if (wrapped < 0) {
        wrapped += Math.PI * 2;
    }

    return wrapped;
}

/** 构造角度尺寸布局（顶点 + 两方向 + 标注位置决定弧段）。 */
function createAngularLayout(
    vertex: SketchDimensionLayoutVec2,
    firstDirection: SketchDimensionLayoutVec2,
    secondDirection: SketchDimensionLayoutVec2,
    label: SketchDimensionLayoutVec2,
    hideExtensionLines = false,
): SketchDimensionLayout {
    const radiusVector = subtract(label, vertex);
    const radius = norm(radiusVector);

    if (radius < 1e-9) {
        return createTextOnlyLayout(label);
    }

    /**
     * 两条无限直线的四条射线方向把全角切成四段；
     * 绘制"包含标注方向"的那一段弧（对照 SOLIDWORKS 放置侧决定角域）。
     */
    const rayAngles = [
        wrapTwoPi(Math.atan2(firstDirection[1], firstDirection[0])),
        wrapTwoPi(Math.atan2(secondDirection[1], secondDirection[0])),
        wrapTwoPi(Math.atan2(-firstDirection[1], -firstDirection[0])),
        wrapTwoPi(Math.atan2(-secondDirection[1], -secondDirection[0])),
    ].sort((left, right) => left - right);
    const labelAngle = wrapTwoPi(Math.atan2(radiusVector[1], radiusVector[0]));
    let startAngle = rayAngles[rayAngles.length - 1]!;
    let endAngle = rayAngles[0]! + Math.PI * 2;

    for (let index = 0; index < rayAngles.length; index += 1) {
        const sectorStart = rayAngles[index]!;
        const sectorEnd = index + 1 < rayAngles.length ? rayAngles[index + 1]! : rayAngles[0]! + Math.PI * 2;
        const normalizedLabel = labelAngle < sectorStart ? labelAngle + Math.PI * 2 : labelAngle;

        if (normalizedLabel >= sectorStart && normalizedLabel <= sectorEnd) {
            startAngle = sectorStart;
            endAngle = sectorEnd;
            break;
        }
    }

    const arcStart: SketchDimensionLayoutVec2 = [
        vertex[0] + Math.cos(startAngle) * radius,
        vertex[1] + Math.sin(startAngle) * radius,
    ];
    const arcEnd: SketchDimensionLayoutVec2 = [
        vertex[0] + Math.cos(endAngle) * radius,
        vertex[1] + Math.sin(endAngle) * radius,
    ];
    /** 界线：顶点沿弧端方向的短延长（保证弧端可读；可按显示属性隐藏）。 */
    const segments: SketchDimensionLayoutSegment[] = hideExtensionLines
        ? []
        : [
            {start: vertex, end: arcStart},
            {start: vertex, end: arcEnd},
        ];
    /** 箭头沿弧切向。 */
    const arrows: SketchDimensionLayoutArrow[] = [
        {position: arcStart, direction: [-Math.sin(startAngle), Math.cos(startAngle)]},
        {position: arcEnd, direction: [Math.sin(endAngle), -Math.cos(endAngle)]},
    ];

    return {
        segments,
        arcs: [{center: vertex, radius, startAngle, endAngle}],
        arrows,
        textPosition: label,
    };
}

/**
 * 构造三点角尺寸布局（两条**射线**围成的角域，允许优角）。
 *
 * SW 语义：**弧永远张成尺寸的数值**——从第一射线扫到第二射线，两种走向里
 * 取跨度与数值吻合的那一支；标注位置只决定弧半径（拖到角域另一侧时，
 * 尺寸界线沿射线延长去接弧，弧本身不变）。
 * 曾按"标注落在哪一侧"选角域：270° 的尺寸把标签拖进 90° 补角侧，
 * 弧当场变成 90° 那段，与数字对不上。
 *
 * @param vertex 角顶点。
 * @param firstDirection 第一射线单位方向。
 * @param secondDirection 第二射线单位方向。
 * @param measuredAngle 尺寸数值（弧度，>0；驱动取名义值、从动取测量值）。
 * @param label 标注位置。
 * @param hideExtensionLines 是否隐藏尺寸界线。
 * @returns 布局。
 */
function createRayAngularLayout(
    vertex: SketchDimensionLayoutVec2,
    firstDirection: SketchDimensionLayoutVec2,
    secondDirection: SketchDimensionLayoutVec2,
    measuredAngle: number,
    label: SketchDimensionLayoutVec2,
    hideExtensionLines = false,
): SketchDimensionLayout {
    const radiusVector = subtract(label, vertex);
    const radius = norm(radiusVector);

    if (radius < 1e-9) {
        return createTextOnlyLayout(label);
    }

    const firstAngle = wrapTwoPi(Math.atan2(firstDirection[1], firstDirection[0]));
    const secondAngle = wrapTwoPi(Math.atan2(secondDirection[1], secondDirection[0]));
    /** 逆时针从第一射线扫到第二射线的跨度 ∈ [0, 2π)。 */
    const counterClockwiseSpan = wrapTwoPi(secondAngle - firstAngle);
    /** 数值收进 (0, 2π)；非法值按逆时针跨度兜底（弧至少落在射线之间）。 */
    const targetSpan = Number.isFinite(measuredAngle) && measuredAngle > 1e-9
        ? Math.min(Math.abs(measuredAngle) % (Math.PI * 2) || Math.PI * 2, Math.PI * 2)
        : counterClockwiseSpan;
    /** 两种走向（逆时针 span / 顺时针 2π−span），画跨度与数值吻合的那一支。 */
    const sweep = Math.abs(counterClockwiseSpan - targetSpan)
        <= Math.abs((Math.PI * 2 - counterClockwiseSpan) - targetSpan)
        ? counterClockwiseSpan
        : counterClockwiseSpan - Math.PI * 2;
    const startAngle = firstAngle;
    const endAngle = firstAngle + sweep;
    const arcStart: SketchDimensionLayoutVec2 = [
        vertex[0] + Math.cos(startAngle) * radius,
        vertex[1] + Math.sin(startAngle) * radius,
    ];
    const arcEnd: SketchDimensionLayoutVec2 = [
        vertex[0] + Math.cos(endAngle) * radius,
        vertex[1] + Math.sin(endAngle) * radius,
    ];
    const segments: SketchDimensionLayoutSegment[] = hideExtensionLines
        ? []
        : [
            {start: vertex, end: arcStart},
            {start: vertex, end: arcEnd},
        ];
    /** 箭头沿弧切向，方向随走向翻转。 */
    const tangentSign = sweep >= 0 ? 1 : -1;
    const arrows: SketchDimensionLayoutArrow[] = [
        {position: arcStart, direction: [tangentSign * Math.sin(startAngle), -tangentSign * Math.cos(startAngle)]},
        {position: arcEnd, direction: [-tangentSign * Math.sin(endAngle), tangentSign * Math.cos(endAngle)]},
    ];
    const arcs: SketchDimensionLayoutArc[] = [{center: vertex, radius, startAngle, endAngle}];
    /**
     * 标注拖出弧跨度时，沿同一圆周从**最近的弧端**延伸一段引出弧指到标注处（SW 同款）：
     * 弧本身张成数值不动，缺这段延长线时标注悬在空中、看不出属于哪段弧。
     */
    const labelAngle = Math.atan2(radiusVector[1], radiusVector[0]);
    const labelOffsetFromStart = sweep >= 0
        ? wrapTwoPi(labelAngle - startAngle)
        : wrapTwoPi(startAngle - labelAngle);
    const labelInsideArc = labelOffsetFromStart <= Math.abs(sweep);

    if (!labelInsideArc) {
        const deltaFromStart = wrapToPi(labelAngle - startAngle);
        const deltaFromEnd = wrapToPi(labelAngle - endAngle);
        const [anchorAngle, delta] = Math.abs(deltaFromStart) <= Math.abs(deltaFromEnd)
            ? [startAngle, deltaFromStart]
            : [endAngle, deltaFromEnd];

        arcs.push({center: vertex, radius, startAngle: anchorAngle, endAngle: anchorAngle + delta});
    }

    return {
        segments,
        arcs,
        arrows,
        textPosition: label,
    };
}

/** 构造半径/直径尺寸布局。 */
function createRadialLayout(
    center: SketchDimensionLayoutVec2,
    radius: number,
    label: SketchDimensionLayoutVec2,
    diametric: boolean,
): SketchDimensionLayout {
    const outward = normalize(subtract(label, center)) ?? [1, 0];
    const nearRim = add(center, scale(outward, radius));
    const segments: SketchDimensionLayoutSegment[] = [];
    const arrows: SketchDimensionLayoutArrow[] = [
        {position: nearRim, direction: scale(outward, -1)},
    ];

    if (diametric) {
        const farRim = add(center, scale(outward, -radius));

        segments.push({start: farRim, end: nearRim});
        arrows.push({position: farRim, direction: outward});
    } else {
        segments.push({start: center, end: nearRim});
    }
    // 圆周到标注文字的引线。
    if (norm(subtract(label, nearRim)) > 1e-9) {
        segments.push({start: nearRim, end: label});
    }

    return {segments, arcs: [], arrows, textPosition: label};
}

/**
 * 构造圆系尺寸的**线性式**布局（SW 右键 Display Options→Display as linear）。
 *
 * 语义：只改标注形状，不碰求解参数与显示数值——
 * 直径式跨整圆、径向式圆心到圆周，线性式则把同一段量画成带尺寸界线的线性尺寸
 * （旋转轮廓图纸的常见画法）。
 *
 * 测量方向由标注位置决定：标注偏在圆的上/下方 → 横向量取；偏在左/右 → 纵向量取，
 * 与线性尺寸"尺寸线垂直于标注偏移方向"的直觉一致。
 *
 * @param center 圆心。
 * @param radius 半径。
 * @param label 标注位置。
 * @param diametric 是否直径语义（true=跨整圆，false=圆心到圆周）。
 * @param hideExtensionLines 是否隐藏尺寸界线。
 * @returns 线性式布局。
 */
function createRadialLinearLayout(
    center: SketchDimensionLayoutVec2,
    radius: number,
    label: SketchDimensionLayoutVec2,
    diametric: boolean,
    hideExtensionLines: boolean,
): SketchDimensionLayout {
    const labelOffset = subtract(label, center);
    const direction: SketchDimensionLayoutVec2 = Math.abs(labelOffset[1]) >= Math.abs(labelOffset[0])
        ? [1, 0]
        : [0, 1];
    const sideSign = dot(labelOffset, direction) >= 0 ? 1 : -1;
    const first = diametric
        ? add(center, scale(direction, -radius))
        : center;
    const second = diametric
        ? add(center, scale(direction, radius))
        : add(center, scale(direction, radius * sideSign));

    return createLinearLayout(first, second, direction, label, hideExtensionLines);
}

/**
 * 解析圆系尺寸的有效显示样式。
 *
 * 未覆盖时按核心类型默认（diameter→直径式、radius→径向式），与文本前缀同一套判定
 * （`SketchDimensionTextFormatter`），保证形状与前缀始终一致。
 *
 * @param snapshot 尺寸快照。
 * @returns 显示样式。
 */
function resolveRadialDisplayStyle(snapshot: SketchDimensionSnapshot): 'linear' | 'radial' | 'diametric' {
    return snapshot.display.displayStyle
        ?? (snapshot.dimensionKind === 'diameter' ? 'diametric' : 'radial');
}

/**
 * 构造样条总长布局（引线式）。
 *
 * 语义：总长是整条曲线的量，没有可标注的"两端投影"，因此对齐 SOLIDWORKS 的引线式
 * 标注——从曲线上离标注最近的点引一条引线到文字，并在曲线侧落一个箭头。
 *
 * @param curve 采样折线。
 * @param label 标注位置。
 * @returns 引线式布局。
 */
function createLeaderLayout(
    curve: readonly SketchDimensionLayoutVec2[],
    label: SketchDimensionLayoutVec2,
): SketchDimensionLayout {
    let nearest = curve[0]!;
    let nearestDistance = Number.POSITIVE_INFINITY;

    curve.forEach((point) => {
        const distance = norm(subtract(point, label));

        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = point;
        }
    });

    const direction = normalize(subtract(label, nearest));

    if (!direction) {
        return createTextOnlyLayout(label);
    }

    return {
        segments: [{start: nearest, end: label}],
        arcs: [],
        arrows: [{position: nearest, direction}],
        textPosition: label,
    };
}

/** 按锚点种类读取点位（点锚点取自身；圆锚点取圆心；椭圆取中心；抛物线取顶点；线锚点取起点；折线取首点）。 */
function anchorPoint(anchor: SketchDimensionReferenceAnchor | undefined): SketchDimensionLayoutVec2 | null {
    if (!anchor) {
        return null;
    }
    if (anchor.kind === 'point') {
        return anchor.position;
    }
    if (anchor.kind === 'circle') {
        return anchor.center;
    }
    if (anchor.kind === 'ellipse') {
        return anchor.center;
    }
    if (anchor.kind === 'parabola') {
        return anchor.apex;
    }
    if (anchor.kind === 'polyline') {
        return anchor.points[0] ?? null;
    }

    return anchor.start;
}

/** 读取线锚点。 */
function lineAnchor(
    anchors: readonly SketchDimensionReferenceAnchor[],
    index: number,
): Extract<SketchDimensionReferenceAnchor, {kind: 'line'}> | null {
    const anchor = anchors[index];

    return anchor?.kind === 'line' ? anchor : null;
}

/** 点到无限直线的垂足。 */
function projectPointToLine(
    point: SketchDimensionLayoutVec2,
    line: Extract<SketchDimensionReferenceAnchor, {kind: 'line'}>,
): SketchDimensionLayoutVec2 | null {
    const direction = normalize(subtract(line.end, line.start));

    if (!direction) {
        return null;
    }

    return add(line.start, scale(direction, dot(subtract(point, line.start), direction)));
}

/**
 * 计算目标点在线段上的最近点（参数投影后夹取到线段域内）。
 *
 * @param point 目标点（草图局部坐标，通常为标注位置）。
 * @param line 目标线段锚点。
 * @returns 线段域内的投影点；线段退化时返回起点。
 */
function clampPointToSegment(
    point: SketchDimensionLayoutVec2,
    line: Extract<SketchDimensionReferenceAnchor, {kind: 'line'}>,
): SketchDimensionLayoutVec2 {
    const direction = subtract(line.end, line.start);
    const lengthSquared = dot(direction, direction);

    if (lengthSquared < 1e-18) {
        return line.start;
    }

    const t = Math.max(0, Math.min(1, dot(subtract(point, line.start), direction) / lengthSquared));

    return add(line.start, scale(direction, t));
}

/**
 * 尺寸布局解析器（纯函数）。
 *
 * 设计说明：
 * 1. 只消费纯数据（快照 + 引用锚点），输出直线段/弧/箭头/文字位置，
 *    渲染器负责局部 → 世界换算与 Three 对象增量维护（02 计划 §10.1 职责分层）；
 * 2. 引用几何缺失（悬空等）时显式降级为"仅文字"布局，不抛错、不猜测；
 * 3. 布局全部由领域主状态派生（标注位置 = display.labelPosition），
 *    渲染结果不反向成为事实来源。
 *
 * @param snapshot 尺寸快照。
 * @param anchors 按引用顺序解析的几何锚点（不可解析的引用为 undefined）。
 * @returns 尺寸布局。
 */
export function resolveSketchDimensionLayout(
    snapshot: SketchDimensionSnapshot,
    anchors: readonly (SketchDimensionReferenceAnchor | undefined)[],
): SketchDimensionLayout {
    const label = snapshot.display.labelPosition ?? anchorPoint(anchors[0]) ?? [0, 0];
    const hideExtensionLines = snapshot.display.extensionLinesHidden === true;

    switch (snapshot.dimensionKind) {
        case 'point-distance': {
            const first = anchorPoint(anchors[0]);
            const second = anchorPoint(anchors[1]);

            if (!first || !second) {
                return createTextOnlyLayout(label);
            }

            //------ 尺寸链（Ordinate）布局 开始------
            /**
             * 尺寸链（SW 坐标尺寸）：first=基准点、second=目标点。
             * 引导线过基准点沿测量方向；目标界线从几何点拉到引导线；
             * 单向箭头沿测量方向指向目标界线；首条附带基准记号圆圈 + "0" 标注。
             * 标注位置的法向分量决定引导线偏移（默认 0 = 引导线过基准点）。
             */
            if (snapshot.branch.ordinate === true) {
                const ordinateOrientation = snapshot.branch.orientation;
                const rawDirection: SketchDimensionLayoutVec2 | null = ordinateOrientation === 'horizontal'
                    ? [1, 0]
                    : ordinateOrientation === 'vertical'
                        ? [0, 1]
                        : snapshot.branch.fixedDirection
                            ? normalize([snapshot.branch.fixedDirection[0], snapshot.branch.fixedDirection[1]])
                            : normalize(subtract(second, first));

                if (!rawDirection) {
                    return createTextOnlyLayout(label);
                }

                const ordNormal: SketchDimensionLayoutVec2 = [-rawDirection[1], rawDirection[0]];
                /** 引导线法向偏移（标注被拖动时跟随；初始 0 = 过基准点）。 */
                const guideOffset = dot(subtract(label, first), ordNormal);
                const guideBase = add(first, scale(ordNormal, guideOffset));
                const signedProjection = dot(subtract(second, first), rawDirection);
                const targetOnGuide = add(guideBase, scale(rawDirection, signedProjection));
                const segments: SketchDimensionLayoutSegment[] = [
                    // 引导线：基准 → 目标投影。
                    {start: guideBase, end: targetOnGuide},
                ];

                // 目标界线：几何点 → 引导线（重合时省略）。
                if (!hideExtensionLines && norm(subtract(second, targetOnGuide)) > 1e-9) {
                    segments.push({start: second, end: targetOnGuide});
                }

                const arrowDirection = scale(rawDirection, signedProjection >= 0 ? 1 : -1);
                const layoutResult: SketchDimensionLayout = {
                    segments,
                    arcs: [],
                    // 单向箭头：位于目标界线处、沿测量方向指外（尖端朝界线）。
                    arrows: [{position: targetOnGuide, direction: scale(arrowDirection, -1)}],
                    textPosition: targetOnGuide,
                    ...(snapshot.branch.ordinateBaseline === true
                        ? {
                            auxiliaryLabels: [{
                                position: add(guideBase, scale(arrowDirection, -0.002)),
                                text: '0',
                            }],
                        }
                        : {}),
                };

                if (snapshot.branch.ordinateBaseline === true) {
                    // 基准记号：引导线起点的小圆圈（12 段近似）；基准界线到几何点。
                    const markRadius = 0.0012;
                    const circleSegments: SketchDimensionLayoutSegment[] = [];

                    for (let index = 0; index < 12; index += 1) {
                        const fromAngle = (index / 12) * Math.PI * 2;
                        const toAngle = ((index + 1) / 12) * Math.PI * 2;

                        circleSegments.push({
                            start: [
                                guideBase[0] + Math.cos(fromAngle) * markRadius,
                                guideBase[1] + Math.sin(fromAngle) * markRadius,
                            ],
                            end: [
                                guideBase[0] + Math.cos(toAngle) * markRadius,
                                guideBase[1] + Math.sin(toAngle) * markRadius,
                            ],
                        });
                    }
                    segments.push(...circleSegments);
                    if (!hideExtensionLines && norm(subtract(first, guideBase)) > 1e-9) {
                        segments.push({start: first, end: guideBase});
                    }
                }

                return layoutResult;
            }
            //------ 尺寸链布局 结束------

            const orientation = snapshot.branch.orientation ?? 'aligned';
            /** 链尺寸固定方向优先：整条链的尺寸线全部平行于继承方向（SW 语义）。 */
            const fixedDirection = snapshot.branch.fixedDirection
                ? normalize([snapshot.branch.fixedDirection[0], snapshot.branch.fixedDirection[1]])
                : null;
            const direction = fixedDirection
                ?? (orientation === 'horizontal'
                    ? [1, 0] as const
                    : orientation === 'vertical'
                        ? [0, 1] as const
                        : normalize(subtract(second, first)));

            return direction
                ? createLinearLayout(first, second, direction, label, hideExtensionLines)
                : createTextOnlyLayout(label);
        }
        case 'length': {
            const line = lineAnchor(anchors, 0);
            /** H/V 尺寸命令固定方向语义（阶段 8）：布局方向随 branch.orientation。 */
            const lengthOrientation = snapshot.branch.orientation ?? 'aligned';
            const direction = !line
                ? null
                : lengthOrientation === 'horizontal'
                    ? [1, 0] as const
                    : lengthOrientation === 'vertical'
                        ? [0, 1] as const
                        : normalize(subtract(line.end, line.start));

            return line && direction
                ? createLinearLayout(line.start, line.end, direction, label, hideExtensionLines)
                : createTextOnlyLayout(label);
        }
        case 'point-line-distance':
        case 'line-line-distance': {
            const isPointLine = snapshot.dimensionKind === 'point-line-distance';
            const pointIndex = isPointLine
                ? (anchors[0]?.kind === 'line' ? 1 : 0)
                : 1;
            const lineIndex = pointIndex === 0 ? 1 : 0;
            let point = anchorPoint(anchors[pointIndex]);
            const line = lineAnchor(anchors, lineIndex);

            if (!point || !line) {
                return createTextOnlyLayout(label);
            }

            if (!isPointLine) {
                /**
                 * 平行线距（等距线段尺寸）：测量点不是固定在线段起点，
                 * 而是标注位置在第二线（等距线）上的投影夹取到线段内的点——
                 * 拖动标注时尺寸线沿两平行线滑动（与椭圆弧锚点同口径），
                 * 点线距保持"测量真实点"语义不变。
                 */
                const slidingLine = lineAnchor(anchors, pointIndex);

                if (slidingLine) {
                    point = clampPointToSegment(label, slidingLine);
                }
            }

            const foot = projectPointToLine(point, line);
            const direction = foot ? normalize(subtract(point, foot)) : null;

            return foot && direction
                ? createLinearLayout(foot, point, direction, label, hideExtensionLines)
                : createTextOnlyLayout(label);
        }
        case 'angle':
        case 'symmetric-angle': {
            const first = lineAnchor(anchors, 0);
            const second = lineAnchor(anchors, 1);
            const firstDirection = first ? normalize(subtract(first.end, first.start)) : null;
            const secondDirection = second ? normalize(subtract(second.end, second.start)) : null;

            if (!first || !second || !firstDirection || !secondDirection) {
                return createTextOnlyLayout(label);
            }

            /** 两线交点作为角顶点（平行时降级为仅文字）。 */
            const denominator = firstDirection[0] * secondDirection[1] - firstDirection[1] * secondDirection[0];

            if (Math.abs(denominator) < 1e-12) {
                return createTextOnlyLayout(label);
            }

            const t = ((second.start[0] - first.start[0]) * secondDirection[1]
                - (second.start[1] - first.start[1]) * secondDirection[0]) / denominator;
            const vertex = add(first.start, scale(firstDirection, t));

            return createAngularLayout(vertex, firstDirection, secondDirection, label, hideExtensionLines);
        }
        case 'three-point-angle': {
            const vertex = anchorPoint(anchors[0]);
            const firstRay = anchorPoint(anchors[1]);
            const secondRay = anchorPoint(anchors[2]);
            const firstDirection = vertex && firstRay ? normalize(subtract(firstRay, vertex)) : null;
            const secondDirection = vertex && secondRay ? normalize(subtract(secondRay, vertex)) : null;
            /** 弧张成尺寸数值（驱动=名义值，从动=测量值），标注位置只决定弧半径。 */
            const measuredAngle = snapshot.role === 'driven'
                ? snapshot.measuredValue ?? snapshot.parameter.nominalValue
                : snapshot.parameter.nominalValue;

            return vertex && firstDirection && secondDirection
                ? createRayAngularLayout(
                    vertex, firstDirection, secondDirection, measuredAngle, label, hideExtensionLines,
                )
                : createTextOnlyLayout(label);
        }
        case 'radius':
        case 'diameter': {
            const circle = anchors[0]?.kind === 'circle' ? anchors[0] : null;

            if (!circle) {
                return createTextOnlyLayout(label);
            }

            /** 显示样式决定标注形状（DISP-001 核心/显示分离：改样式不改求解参数）。 */
            const style = resolveRadialDisplayStyle(snapshot);

            if (style === 'linear') {
                return createRadialLinearLayout(
                    circle.center,
                    circle.radius,
                    label,
                    snapshot.dimensionKind === 'diameter',
                    hideExtensionLines,
                );
            }

            return createRadialLayout(circle.center, circle.radius, label, style === 'diametric');
        }
        case 'spline-length': {
            const curve = anchors[0]?.kind === 'polyline' ? anchors[0].points : null;

            return curve && curve.length >= 2
                ? createLeaderLayout(curve, label)
                : createTextOnlyLayout(label);
        }
        case 'path-length': {
            /**
             * 路径总长与样条总长同款引线式标注（SW）：总长是整条链的量，
             * 没有可标注的"两端投影"——从链上离标注最近的点引一条引线到文字。
             * 逐段取标注在线段上的垂足（夹取到段内）作为候选，交给引线布局
             * 选全局最近点。曾无此分支走 default → 落库后只剩数值没有尺寸线。
             */
            const nearestPoints: SketchDimensionLayoutVec2[] = [];

            anchors.forEach((anchor) => {
                if (anchor?.kind === 'line') {
                    const direction = subtract(anchor.end, anchor.start);
                    const lengthSquared = dot(direction, direction);

                    if (lengthSquared < 1e-18) {
                        return;
                    }

                    const t = Math.max(0, Math.min(1, dot(subtract(label, anchor.start), direction) / lengthSquared));

                    nearestPoints.push(add(anchor.start, scale(direction, t)));
                    return;
                }
                // 弧段引用解析为圆锚点：最近点取圆周上朝标注方向的点（近似，不含弧域裁剪）。
                if (anchor?.kind === 'circle') {
                    const outward = normalize(subtract(label, anchor.center));

                    if (outward) {
                        nearestPoints.push(add(anchor.center, scale(outward, anchor.radius)));
                    }
                    return;
                }
                // 样条段引用解析为采样折线：逐采样点取最近。
                if (anchor?.kind === 'polyline') {
                    anchor.points.forEach((point) => nearestPoints.push(point));
                }
            });

            return nearestPoints.length > 0
                ? createLeaderLayout(nearestPoints, label)
                : createTextOnlyLayout(label);
        }
        case 'curve-offset-distance': {
            // 曲线对偏移距离：圆/弧取圆周上朝标注方向的径向点对，
            // 椭圆取长轴正向端点对；两锚点连线即尺寸线（同心曲线径向间隙）。
            const readAnchorPoint = (anchor: SketchDimensionReferenceAnchor | undefined): SketchDimensionLayoutVec2 | null => {
                // 点锚点：抛物线等距（方案A）输出样条后，源侧锚点改为
                // 源抛物线首采样点，与样条侧首控制点（首采样点）配对——
                // 尺寸线两端与名义值测量点（首采样段法向距离）一致。
                if (anchor?.kind === 'point') {
                    return anchor.position;
                }
                if (anchor?.kind === 'circle') {
                    const outward = normalize(subtract(label, anchor.center)) ?? [1, 0] as const;

                    return add(anchor.center, scale(outward, anchor.radius));
                }
                if (anchor?.kind === 'ellipse') {
                    // 椭圆弧：锚点跟随标注方向，且夹取到弧实际扫过的角域内
                    // （对齐圆/弧锚点"朝标注方向"的口径，修复锚点钉死在长轴正向四分点）。
                    return resolveEllipseAnchorPointToward(anchor, label);
                }
                // 抛物线对（旧等距抛物线）：以顶点为锚点（两顶点连线即尺寸线）
                if (anchor?.kind === 'parabola') {
                    return anchor.apex;
                }
                // 样条对：以首控制点（首采样点）为锚点
                if (anchor?.kind === 'polyline' && anchor.points.length > 0) {
                    return anchor.points[0] ?? null;
                }

                return null;
            };
            const first = readAnchorPoint(anchors[0]);
            const second = readAnchorPoint(anchors[1]);

            if (!first || !second) {
                return createTextOnlyLayout(label);
            }

            const direction = normalize(subtract(second, first)) ?? [1, 0] as const;

            return createLinearLayout(first, second, direction, label, hideExtensionLines);
        }
        default:
            return createTextOnlyLayout(label);
    }
}

/**
 * 生成箭头的两条 V 形短线（渲染器直接画线段）。
 *
 * @param arrow 箭头。
 * @returns 两条短线段。
 */
export function expandSketchDimensionArrow(arrow: SketchDimensionLayoutArrow): readonly SketchDimensionLayoutSegment[] {
    const direction = arrow.direction;
    const normal: SketchDimensionLayoutVec2 = [-direction[1], direction[0]];
    const back = add(arrow.position, scale(direction, ARROW_LENGTH));
    const half = ARROW_LENGTH * 0.38;

    return [
        {start: arrow.position, end: add(back, scale(normal, half))},
        {start: arrow.position, end: add(back, scale(normal, -half))},
    ];
}
